// BillingAddressForm.tsx
import React, { useEffect, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { Country, IState, State } from 'country-state-city';
import { dropdownKeyEventHandler } from 'renderer/shared/callbacks/keyEventHandler';
import { AddressForm } from '../schemas/paymentMethods.schemas';

interface BillingAddressFormProps {
  disabled: boolean;
}

const blacklist = [
  'Aland Islands',
  'French Southern Territories',
  'Guernsey and Alderney',
  'Heard Island and McDonald Islands',
  'Macau S.A.R.',
  'Palestinian Territory Occupied',
  'Pitcairn Island',
  'Saint-Barthelemy',
  'South Georgia',
  'Vatican City State (Holy See)',
  'Virgin Islands (British)',
];

// Sort countries alphabetically, ensuring USA is first
const countries = Country.getAllCountries()
  .filter((a) => !blacklist.includes(a.name))
  .sort((a, b) => {
    if (a.isoCode === 'US') return -1;
    if (b.isoCode === 'US') return 1;
    return a.name.localeCompare(b.name);
  });

// Manual regions for US Virgin Islands
const VIRGIN_ISLANDS_REGIONS: IState[] = [
  {
    name: 'Saint Croix',
    isoCode: 'SC',
    countryCode: 'VI',
    latitude: '',
    longitude: '',
  },
  {
    name: 'Saint Thomas',
    isoCode: 'ST',
    countryCode: 'VI',
    latitude: '',
    longitude: '',
  },
  {
    name: 'Saint John',
    isoCode: 'SJ',
    countryCode: 'VI',
    latitude: '',
    longitude: '',
  },
];

const BillingAddressForm: React.FC<BillingAddressFormProps> = ({
  disabled,
}) => {
  const { values, setFieldValue } = useFormikContext<AddressForm>();
  const [regions, setRegions] = useState<IState[]>([]);

  useEffect(() => {
    if (!values.countryCode) return;

    // Special-case US Virgin Islands
    if (values.countryCode === 'VI') {
      setRegions(VIRGIN_ISLANDS_REGIONS);
      setFieldValue('countryName', 'Virgin Islands (US)');
      return;
    }

    const countryRegions = State.getStatesOfCountry(values.countryCode);

    setRegions(countryRegions);
    setFieldValue(
      'countryName',
      Country.getCountryByCode(values.countryCode)?.name ?? '',
    );
  }, [setFieldValue, values.countryCode]);

  useEffect(() => {
    if (!values.regionCode || !values.countryCode) return;

    // Handle VI separately
    if (values.countryCode === 'VI') {
      const region = VIRGIN_ISLANDS_REGIONS.find(
        (r) => r.isoCode === values.regionCode,
      );
      setFieldValue('region', region?.name ?? '');
      return;
    }

    setFieldValue(
      'region',
      State.getStateByCodeAndCountry(values.regionCode, values.countryCode)
        ?.name ?? '',
    );
  }, [setFieldValue, values.countryCode, values.regionCode]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Field
          disabled={disabled}
          component={TextField}
          fullWidth
          size="small"
          label="Name on Card"
          name="nameOnCard"
        />
      </Grid>
      <Grid item xs={12}>
        <Field
          component={TextField}
          select
          onKeyUp={dropdownKeyEventHandler(
            countries.map(({ isoCode, name }) => ({
              label: name,
              value: isoCode,
            })),
            (option) => setFieldValue('countryCode', option),
          )}
          fullWidth
          size="small"
          disabled={disabled}
          name="countryCode"
          labelId="countryCode"
          label="Country"
        >
          <MenuItem value="" disabled sx={{ color: 'grey' }}>
            <em>Country</em>
          </MenuItem>
          {countries.map((c) => (
            <MenuItem key={c.isoCode} value={c.isoCode}>
              {c.name}
            </MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={12}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="Address"
          name="streetAddress"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="City"
          name="locality"
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          component={TextField}
          onKeyUp={dropdownKeyEventHandler(
            regions.map(({ isoCode, name }) => ({
              label: name,
              value: isoCode,
            })),
            (option) => setFieldValue('regionCode', option),
          )}
          disabled={disabled}
          fullWidth
          select
          size="small"
          id="regionCode"
          name="regionCode"
          labelId="regionCode"
          label="Region"
        >
          <MenuItem value="" disabled sx={{ color: 'grey' }}>
            <em>Region</em>
          </MenuItem>
          {regions.map((r) => (
            <MenuItem key={r.isoCode} value={r.isoCode}>
              {r.name}
            </MenuItem>
          ))}
        </Field>
      </Grid>
      <Grid item xs={12} sm={3}>
        <Field
          component={TextField}
          disabled={disabled}
          fullWidth
          size="small"
          label="Postal Code"
          name="postalCode"
        />
      </Grid>
    </Grid>
  );
};

export default BillingAddressForm;
